<script setup lang="ts">
import InputLabel from "@/components/ui/InputLabel.vue";
import { computed } from "vue";

type Props = {
  modelValue: boolean;
  label: string;
  loading?: boolean;
  disabled?: boolean;
};

type Emits = {
  (e: "update:modelValue", value: boolean): void;
};

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
});

const emit = defineEmits<Emits>();

function toggle(): void {
  if (!props.disabled && !props.loading) {
    emit("update:modelValue", !props.modelValue);
  }
}

const containerClasses = computed(() => {
  const base = ["flex", "items-center", "justify-between", "gap-4", "py-3"];

  if (props.disabled || props.loading) {
    base.push("cursor-not-allowed", "opacity-50");
  } else {
    base.push("cursor-pointer");
  }

  return base;
});

const switchClasses = computed(() => {
  const base = [
    "relative",
    "inline-flex",
    "items-center",
    "h-7",
    "w-12",
    "shrink-0",
    "rounded-full",
    "border-2",
    "border-black-dark",
    "p-0.5",
    "transition-all",
    "duration-200",
    "shadow-sm",
  ];

  if (props.modelValue && !props.disabled && !props.loading) {
    base.push("bg-kekkle-blue", "shadow-blue-hard-sm");
  } else {
    base.push("bg-gray-300", "dark:bg-gray-600");
  }

  return base;
});

const thumbClasses = computed(() => {
  const base = [
    "h-5",
    "w-5",
    "transform",
    "rounded-full",
    "bg-white",
    "transition-transform",
    "duration-200",
    "border-2",
    "border-black-dark",
  ];

  if (props.modelValue) {
    base.push("translate-x-[18px]");
  } else {
    base.push("translate-x-0");
  }

  return base;
});
</script>

<template>
  <div :class="containerClasses" @click="toggle">
    <InputLabel :text="label" />
    <div class="flex items-center gap-2">
      <!-- Loading Spinner -->
      <div
        v-if="loading"
        class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900 dark:border-slate-600 dark:border-t-slate-200"
      ></div>

      <!-- Switch -->
      <div :class="switchClasses">
        <span :class="thumbClasses"></span>
      </div>
    </div>
  </div>
</template>
